import React, { useState } from "react";
import { formatDateFromMillis } from "../../utils/dateUtils";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Roles } from "../../constants/roles";
import DeleteConfirmationModal from "../modals/DeleteConfirmationModal";

const DARK_CARD = {
  backgroundColor: "#FFFFFF",
  border: "1px solid #E2E8F0",
  borderRadius: "12px",
  padding: "20px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
};

const DicomFiles = ({
  dicomInfos,
  handleDownload,
  handleDeleteDicom,
  handleSelect,
  isMobile,
  userRole,
  isDemo = false,
}) => {
  const isViewer = userRole === Roles.VIEWER;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const confirmDelete = (idx) => { setSelectedIndex(idx); setShowDeleteModal(true); };
  const handleConfirmDelete = () => {
    if (selectedIndex !== null) { handleDeleteDicom(selectedIndex); setShowDeleteModal(false); setSelectedIndex(null); }
  };
  const handleCloseModal = () => { setShowDeleteModal(false); setSelectedIndex(null); };

  const iconBtn = (active) => ({
    className: "inline-flex items-center justify-center w-9 h-9 rounded-md transition-colors duration-150",
    style: { color: active ? "#4ADE80" : "#94A3B8" },
    onMouseEnter: (e) => { if (!active) e.currentTarget.style.color = "#0F172A"; },
    onMouseLeave: (e) => { if (!active) e.currentTarget.style.color = "#94A3B8"; },
  });

  // Mobile view
  if (isMobile) {
    return (
      <div style={DARK_CARD}>
        <h2 style={{ fontSize: "18px", fontWeight: 800, color: "#0F172A" }}>DICOM Files</h2>
        <div style={{ height: "1px", backgroundColor: "#F1F5F9", margin: "12px 0 8px" }} />
        <p style={{ fontSize: "13px", color: "#64748B", marginBottom: "12px" }}>
          Only one DICOM file can be displayed in the viewer at a time. Please select the file you would like to view.
        </p>
        <div>
          {(!dicomInfos || dicomInfos.length === 0) && (
            <div style={{ padding: "24px 0", fontSize: "13px", color: "#64748B" }}>No DICOM files yet.</div>
          )}
          {dicomInfos?.map((file, idx) => (
            <div key={idx} className="py-4 flex items-start justify-between gap-4" style={{ borderBottom: "1px solid #F1F5F9" }}>
              <div className="flex-1 space-y-1.5">
                {[
                  { label: "Name", val: file.name },
                  { label: "Uploaded By", val: file.uploadedBy },
                  { label: "Upload Date", val: formatDateFromMillis(file.uploadDate) || file.uploadDate },
                ].map(({ label, val }) => (
                  <p key={label} className="leading-6">
                    <span style={{ fontSize: "13px", fontWeight: 700, color: "#475569", marginRight: "4px" }}>{label}:</span>
                    <span style={{ fontSize: "13px", fontWeight: 400, color: "#0F172A" }}>{val}</span>
                  </p>
                ))}
              </div>
              {!isViewer && !isDemo ? (
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => handleSelect(idx)} {...iconBtn(file.isSelected)}>
                    {file.isSelected ? <CheckCircleIcon fontSize="small" /> : <CheckCircleOutlinedIcon fontSize="small" />}
                  </button>
                  <button onClick={() => handleDownload(file)} {...iconBtn(false)} title="Download">
                    <FileDownloadOutlinedIcon fontSize="small" />
                  </button>
                  <button onClick={() => confirmDelete(idx)} {...iconBtn(false)} title="Delete">
                    <DeleteOutlinedIcon fontSize="small" />
                  </button>
                </div>
              ) : <div className="w-9 h-9" />}
            </div>
          ))}
        </div>
        {showDeleteModal && (
          <DeleteConfirmationModal isOpen={showDeleteModal} closeModal={handleCloseModal} handleDelete={handleConfirmDelete} entityName="this DICOM file" />
        )}
      </div>
    );
  }

  // Desktop view
  return (
    <div className="mt-4 font-inter">
      <h2 style={{ fontSize: "22px", fontWeight: 800, color: "#0F172A", marginBottom: "6px" }}>DICOM Files</h2>
      <p style={{ fontSize: "14px", color: "#94A3B8", marginBottom: "12px" }}>
        Only one DICOM file can be displayed in the viewer at a time. Please select the file you would like to view.
      </p>
      <table className="w-full border-collapse text-[15px] font-[600]" style={{ borderRadius: "12px", overflow: "hidden", backgroundColor: "#FFFFFF", border: "1px solid #E2E8F0" }}>
        <thead>
          <tr style={{ backgroundColor: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>
            {["Select", "Name", "Uploaded By", "Upload Date", ...(!isViewer && !isDemo ? ["Actions"] : [])].map((h) => (
              <th key={h} className="p-3 text-center" style={{ color: "#94A3B8", fontSize: "13px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dicomInfos.map((file, idx) => (
            <tr
              key={idx}
              style={{
              backgroundColor: idx % 2 === 1 ? "#F8FAFC" : "transparent",
                borderBottom: "1px solid #F1F5F9",
                color: "#475569",
              }}
            >
              <td className="p-3 text-center">
                <button
                  className="px-2 py-1 rounded transition-colors duration-150"
                  style={{ color: file.isSelected ? "#4ADE80" : "#64748B" }}
                  onClick={() => handleSelect(idx)}
                >
                  {file.isSelected ? <CheckCircleIcon /> : <CheckCircleOutlinedIcon />}
                </button>
              </td>
              <td className="p-3 text-center" style={{ color: "#0F172A" }}>{file.name}</td>
              <td className="p-3 text-center">{file.uploadedBy}</td>
              <td className="p-3 text-center">{formatDateFromMillis(file.uploadDate) || file.uploadDate}</td>
              {!isViewer && !isDemo && (
                <td className="p-3 text-center space-x-2">
                  <button
                    className="px-2 py-1 rounded transition-colors"
                    style={{ color: "#94A3B8" }}
                    onMouseEnter={(e) => e.currentTarget.style.color = "#60A5FA"}
                    onMouseLeave={(e) => e.currentTarget.style.color = "#94A3B8"}
                    onClick={() => handleDownload(file)}
                  ><FileDownloadOutlinedIcon /></button>
                  <button
                    className="px-2 py-1 rounded transition-colors"
                    style={{ color: "#94A3B8" }}
                    onMouseEnter={(e) => e.currentTarget.style.color = "#F87171"}
                    onMouseLeave={(e) => e.currentTarget.style.color = "#94A3B8"}
                    onClick={() => confirmDelete(idx)}
                  ><DeleteOutlinedIcon /></button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {showDeleteModal && (
        <DeleteConfirmationModal isOpen={showDeleteModal} closeModal={handleCloseModal} handleDelete={handleConfirmDelete} entityName="this DICOM file" />
      )}
    </div>
  );
};

export default DicomFiles;
